import type {TriggerIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import CohortFormMediaTypeLabel from '@cohort/merchants/apps/cohort-form/components/CohortFormMediaTypeLabel';
import StepContentInput from '@cohort/merchants/apps/common/StepContentInput';
import StepDescriptionInput from '@cohort/merchants/apps/common/StepDescriptionInput';
import StepStatementInput from '@cohort/merchants/apps/common/StepStatementInput';
import StepTitleInput from '@cohort/merchants/apps/common/StepTitleInput';
import TriggerReferenceIdInput from '@cohort/merchants/apps/common/TriggerReferenceIdInput';
import type {Defaults} from '@cohort/merchants/apps/common/triggers/utils';
import {defaultMedia, parseDefaults} from '@cohort/merchants/apps/common/triggers/utils';
import LanguageSelectorInput from '@cohort/merchants/components/form/LanguageSelectorInput';
import {RadioCards} from '@cohort/merchants/components/form/RadioCards';
import Separator from '@cohort/merchants/components/Separator';
import type {CohortFormMediaTriggerStruct} from '@cohort/shared/apps/cohort-form/triggers/media';
import type {Language} from '@cohort/shared/schema/common';
import {CohortFormMediaTypeSchema} from '@cohort/shared/schema/common/cohortForm';
import {Fragment, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';

const getDefaults = (definedLanguages: Language[]): Defaults => {
  const defaults = {
    title: {},
    description: {},
  };
  return parseDefaults(defaults, definedLanguages);
};

const CohortFormMediaTriggerConfigComponent: React.FC<
  TriggerIntegrationConfigComponentProps<CohortFormMediaTriggerStruct>
> = ({formReturn: {reset, control, register, watch}, step, trigger}) => {
  const init = useRef(false);

  const {t} = useTranslation('app-cohort-form', {
    keyPrefix: 'triggerIntegrations.media.configComponent',
  });

  const [definedLanguages] = watch(['definedLanguages']);

  useEffect(() => {
    if (init.current) {
      return;
    }
    const defaults = getDefaults(definedLanguages);
    reset(formValues => ({
      ...formValues,
      defaultTitle: defaults.title,
      defaultDescription: defaults.description,
      descriptionMode: 'custom',
      title: step?.title ?? {},
      description: step?.description ?? {},
      icon: step?.icon !== undefined ? step.icon : '📸',
      media: step?.media ?? defaultMedia,
      trigger: {
        id: trigger?.id ?? null,
        connectionId: null,
        triggerIntegrationId: 'cohort-form.media',
        triggerIntegrationConfig: {
          referenceId: trigger?.triggerIntegrationConfig.referenceId ?? null,
          mediaType: trigger?.triggerIntegrationConfig.mediaType ?? 'image',
        },
      },
    }));
    init.current = true;
  }, [definedLanguages, reset, step, trigger]);

  const mediaTypesOptions = CohortFormMediaTypeSchema.options.map(type => ({
    label: <CohortFormMediaTypeLabel type={type} />,
    value: type,
  }));

  if (!init.current) {
    return null;
  }

  return (
    <Fragment>
      <LanguageSelectorInput />
      <StepTitleInput />
      <StepDescriptionInput />
      <RadioCards
        name="trigger.triggerIntegrationConfig.mediaType"
        direction="row"
        label={t('labelMediaType')}
        register={register}
        control={control}
        className="grid grid-cols-3 gap-4"
        options={mediaTypesOptions}
        withCheckIcon={false}
      />
      <Separator />
      <StepContentInput />
      <Separator />
      <StepStatementInput />
      <Separator />
      <TriggerReferenceIdInput />
    </Fragment>
  );
};

export default CohortFormMediaTriggerConfigComponent;
